import React, { memo } from 'react';

import { WorkLine } from '@paragon/contract-library';
import { getModifierKeys } from '@paragon/utilities';

import { useAppDispatch } from '../../components/hooks';
import { convertFromLayoutToDesignCoordinates } from '../../library/layout-geometry/coordinates';
import { calculateWorkLineVisualSegment, isInfiniteWorkLine } from '../../library/layout-geometry/work-lines';
import {
  clearHoveredWorkLineIfActive,
  setHoveredWorkLineIfApplicable,
} from '../../store/actions/layout/workLineActions';
import {
  handleStandardSceneItemEditPropertiesAction,
  handleStandardSceneItemRightClick,
} from '../../store/actions/sceneInteractionActions';
import { selectItem } from '../../store/actions/selectActions';
import { clearHoverItemIfActive, ensureHoverItemIsActive } from '../../store/actions/viewportActions';
import { threeJsDom } from '../util/events/ThreeJSDom';
import { useViewCenter } from '../viewCenter';
import { layoutColors } from './layoutColors';
import { raycastWorkLine } from './raycastWorkLine';
import { Segment } from './Segments';

export const INSERTION_WORK_LINE_GUID = '0';

interface WorkLineVisualProps {
  line: WorkLine;
  emphasised?: boolean;
}

export const WorkLineVisual = memo(function (props: WorkLineVisualProps) {
  const dispatch = useAppDispatch();

  const viewCenter = useViewCenter(isInfiniteWorkLine(props.line));
  const segment = calculateWorkLineVisualSegment(props.line, viewCenter);

  const hoverItem = { type: 'workLine', guid: props.line.guid, origin: 'fiber' } as const;

  return (
    <group
      userData={{ raycastSegment: segment }}
      raycast={raycastWorkLine}
      onClick={(event) => {
        if (hoverItem.guid === INSERTION_WORK_LINE_GUID) return;

        if (event.detail === 2) {
          event.stopPropagation();
          dispatch(handleStandardSceneItemEditPropertiesAction('workLine', props.line));
        } else if (threeJsDom.shouldDispatchClickEvent()) {
          event.stopPropagation();
          dispatch(
            selectItem(props.line, 'workLine', { intent: 'toggle', exclusive: !getModifierKeys(event).ctrlKey }),
          );
        }
      }}
      onContextMenu={async (event) => {
        if (hoverItem.guid === INSERTION_WORK_LINE_GUID) return;

        await dispatch(handleStandardSceneItemRightClick(event, hoverItem));
      }}
      onPointerOver={(event) => {
        if (hoverItem.guid === INSERTION_WORK_LINE_GUID) return;

        event.stopPropagation();
        dispatch(ensureHoverItemIsActive(hoverItem));
        dispatch(setHoveredWorkLineIfApplicable(props.line.guid));
      }}
      onPointerOut={() => {
        if (hoverItem.guid === INSERTION_WORK_LINE_GUID) return;

        dispatch(clearHoverItemIfActive(hoverItem));
        dispatch(clearHoveredWorkLineIfActive(props.line.guid));
      }}
    >
      <WorkLineSegment line={props.line} segment={segment} emphasised={props.emphasised} />
    </group>
  );
});

interface WorkLineSegmentsProps {
  lines: readonly WorkLine[];
  emphasised?: boolean;
}

export function WorkLineSegments(props: WorkLineSegmentsProps) {
  const viewCenter = useViewCenter(props.lines.some(isInfiniteWorkLine));

  return props.lines.map((line) => {
    const segment = calculateWorkLineVisualSegment(line, viewCenter);
    return <WorkLineSegment key={line.guid} line={line} segment={segment} emphasised={props.emphasised} />;
  });
}

function WorkLineSegment(props: {
  line: WorkLine;
  segment: ReturnType<typeof calculateWorkLineVisualSegment>;
  emphasised?: boolean;
}) {
  const colors = props.line.bounding ? layoutColors.boundingWorkLines : layoutColors.workLines;

  return (
    <Segment
      start={convertFromLayoutToDesignCoordinates(props.segment.basePoint).toArray()}
      end={convertFromLayoutToDesignCoordinates(props.segment.endPoint).toArray()}
      color={props.emphasised ? colors.hovered : colors.normal}
    />
  );
}
